import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import styled from 'styled-components';
import classNames from 'classnames';
import { Alert } from '@brokerbay/design-components';
import Bounds from './Bounds';
import TimeSelectorButton from './TimeSelectorButton';
import AdvancedSelector from './AdvancedSelector';
import LockboxHoursModal from './LockboxHoursModal';

class WeeklyTimeSelector extends React.Component {
  static get FIRST_TIME() {
    return '7:00';
  }

  static get FIRST_DAY() {
    return 'MON';
  }

  constructor(props) {
    super(props);

    this.timeslotRefs = {};

    this.state = {
      advConfSetting: false,
      selectedRows: [],
      selectedColumns: [],
      selectionBox: {},
      offsetFromRoot: undefined,
    };
  }

  updateComponentOffset = () => {
    // Calculate the component offset from the page so we can draw the selection
    // box in the right place
    const ref = this.componentRef;
    const pageX = ref.getBoundingClientRect().left + window.pageXOffset;
    const pageY = ref.getBoundingClientRect().top + window.pageYOffset;
    this.setState({ timeSelectorOffset: [pageX, pageY] });
  };

  onMouseDown = e => {
    e.preventDefault();
    if (e.button !== 0) {
      // A button other than the main button was pressed; ignore
      return;
    }

    const { timeSelection } = this.props;
    const { advConfSetting } = this.state;

    // Get the position of the component in preparation of future calculations
    this.updateComponentOffset();
    // Get row and column bounds to determine what's selected
    this.updateRowAndColumnBounds();

    // Set starting bounds of the selection box
    const { pageX: x, pageY: y } = e;
    const selectionBox = { start: { x, y }, end: { x, y } };

    // Determine whether this selection is to enable or disable based on the
    // state of the starting element
    const startTarget = Object.keys(this.timeslotRefs).find(key =>
      Bounds.fromElement(this.timeslotRefs[key]).contains({ x, y }),
    );
    if (startTarget) {
      const [startDay, startTime] = startTarget.split('-');
      if (advConfSetting) {
        selectionBox.toValue =
          timeSelection[startDay][startTime] === 'AutoConfirm'
            ? 'Available'
            : 'AutoConfirm';
      } else {
        selectionBox.toValue =
          timeSelection[startDay][startTime] === 'Available' ||
          timeSelection[startDay][startTime] === true
            ? 'Restricted'
            : 'Available';
      }
    } else {
      selectionBox.toValue = advConfSetting ? 'AutoConfirm' : 'Restricted';
    }

    // Update selected rows and columns
    const { selectedRows, selectedColumns } =
      this.getSelectedRowsAndColumns(selectionBox);

    this.setState({
      selectionBox,
      selectedRows,
      selectedColumns,
      mouseDown: true,
    });

    // Add listeners for future mouse action
    window.document.addEventListener('mousemove', this.onMouseMove);
    window.document.addEventListener('mouseup', this.onMouseUp);
  };

  onMouseMove = e => {
    e.preventDefault();

    const { selectionBox } = this.state;
    selectionBox.end = { x: e.pageX, y: e.pageY };

    const { selectedRows, selectedColumns } =
      this.getSelectedRowsAndColumns(selectionBox);

    this.setState({
      selectionBox,
      selectedRows,
      selectedColumns,
    });
  };

  onMouseUp = e => {
    e.preventDefault();

    if (e.button !== 0) {
      // A button other than the main button was pressed; ignore
      return;
    }

    window.document.removeEventListener('mousemove', this.onMouseMove);
    window.document.removeEventListener('mouseup', this.onMouseUp);

    const {
      selectionBox: { toValue },
      selectedRows,
      selectedColumns,
      advConfSetting,
    } = this.state;
    this.setState({
      selectionBox: {},
      selectedRows: [],
      selectedColumns: [],
      mouseDown: false,
    });

    this.processSelection(
      selectedRows,
      selectedColumns,
      toValue,
      advConfSetting,
    );
  };

  updateRowAndColumnBounds = () => {
    this.verticalBounds = Object.entries(this.timeslotRefs)
      .filter(([key]) => key.split('-')[0] === WeeklyTimeSelector.FIRST_DAY)
      .map(([key, element]) => {
        const [, timeslot] = key.split('-');
        const bounds = Bounds.fromElement(element);
        return {
          timeslot,
          top: bounds.top,
          bottom: bounds.bottom,
        };
      });
    this.horizontalBounds = Object.entries(this.timeslotRefs)
      .filter(([key]) => key.split('-')[1] === WeeklyTimeSelector.FIRST_TIME)
      .map(([key, element]) => {
        const [day] = key.split('-');
        const bounds = Bounds.fromElement(element);
        return {
          day,
          left: bounds.left,
          right: bounds.right,
        };
      });
  };

  getSelectedRowsAndColumns = selectionBox => {
    const { start, end } = selectionBox;
    const selectionBounds = Bounds.fromPoints(start, end);
    const selectedRows = (this.verticalBounds || [])
      .filter(row => selectionBounds.intersectsRow(row))
      .map(row => row.timeslot);
    const selectedColumns = (this.horizontalBounds || [])
      .filter(column => selectionBounds.intersectsColumn(column))
      .map(column => column.day);
    return { selectedRows, selectedColumns };
  };

  isLockboxUnavailable = (day, time) => {
    const { supraLockboxRestrictedHoursMap: restrictedHours } = this.props;
    return restrictedHours?.[day]?.includes(time);
  };

  processSelection = (selectedRows, selectedColumns, toValue, autoConf) => {
    const { selectTimes, supraLockboxAccessHours, access } = this.props;
    if (
      access === 'Supra Lockbox' &&
      supraLockboxAccessHours &&
      (toValue === 'Available' || toValue === 'AutoConfirm')
    ) {
      const selectedTimeslots = selectedColumns
        .map(day =>
          selectedRows.map(time => {
            if (this.isLockboxUnavailable(day, time)) return null;
            return [day, time];
          }),
        )
        .flat(1)
        .filter(Boolean);
      selectTimes(selectedTimeslots, toValue, autoConf);
    } else {
      const selectedTimeslots = selectedColumns
        .map(day => selectedRows.map(time => [day, time]))
        .flat(1);
      selectTimes(selectedTimeslots, toValue, autoConf);
    }
  };

  toggleDay = (day, toValue) => {
    const { selectTimes, timeSelection, supraLockboxAccessHours, access } =
      this.props;
    if (
      access === 'Supra Lockbox' &&
      supraLockboxAccessHours &&
      toValue === 'Available'
    ) {
      const selectedTimeslots = Object.keys(timeSelection[day])
        .map(time => {
          if (this.isLockboxUnavailable(day, time)) return null;
          return [day, time];
        })
        .filter(Boolean);
      selectTimes(selectedTimeslots, toValue);
    } else {
      const selectedTimeslots = Object.keys(timeSelection[day]).map(time => [
        day,
        time,
      ]);
      selectTimes(selectedTimeslots, toValue);
    }
  };

  renderSelectionBox = () => {
    const { mouseDown, selectionBox, timeSelectorOffset } = this.state;
    if (!mouseDown) {
      // Only show the selection when user is actively selecting
      return null;
    }

    const { start, end, toValue } = selectionBox;
    const selectionBoxBounds = Bounds.fromPoints(start, end);
    // We need to factor in the component offset because this element is drawn
    // Absolute to the relative parent
    const [offsetX, offsetY] = timeSelectorOffset;
    const selectionBoxStyles = {
      left: selectionBoxBounds.left - offsetX,
      width: selectionBoxBounds.right - selectionBoxBounds.left,
      top: selectionBoxBounds.top - offsetY,
      height: selectionBoxBounds.bottom - selectionBoxBounds.top,
    };
    return (
      <div
        className={classNames('time-selector__selection-box', {
          'time-selector__selection-box--available':
            toValue === 'Available' || toValue === 'AutoConfirm',
          'time-selector__selection-box--restricted': toValue === 'Restricted',
        })}
        id="selection-box"
        style={selectionBoxStyles}
      />
    );
  };

  advAutoConfirmClickHandler = e => {
    this.setState({ advConfSetting: e.target.value === 'advAutoConfEnabled' });
  };

  disableAutoConfirmToggle = () => {
    this.setState({ advConfSetting: false });
  };

  removeAllAutoConfirmSlots = () => {
    const { selectTimes, timeSelection } = this.props;
    const autoConfTimeslots = [];
    Object.keys(timeSelection).forEach(day => {
      Object.keys(timeSelection[day]).forEach(time => {
        if (timeSelection[day][time] === 'AutoConfirm') {
          autoConfTimeslots.push([day, time]);
        }
      });
    });
    selectTimes(autoConfTimeslots, 'Available');
  };

  componentDidUpdate(prevProps, prevState) {
    const { disableAutoConfirm, noMoreShowings } = this.props;
    if (
      (disableAutoConfirm && prevState.advConfSetting) ||
      (noMoreShowings && prevProps.noMoreShowings !== noMoreShowings)
    ) {
      this.disableAutoConfirmToggle();
      if (noMoreShowings && prevProps.noMoreShowings !== noMoreShowings) {
        this.removeAllAutoConfirmSlots();
      }
    }
  }

  getTimeSelectorButtonStatus = (day, dayAvailabilities, timeslot) => {
    const { access, autoConfirm, mapAutoConfAvailabilities } = this.props;
    if (
      access === 'Supra Lockbox' &&
      this.isLockboxUnavailable(day, timeslot)
    ) {
      return 'noLockbox';
    }
    if (autoConfirm) {
      return mapAutoConfAvailabilities(dayAvailabilities)[timeslot];
    }
    return dayAvailabilities[timeslot];
  };

  render() {
    const { advConfSetting, selectedColumns, selectionBox, selectedRows } =
      this.state;
    const {
      disableAutoConfirm,
      timeSelection,
      lockboxHoursModalVisible,
      openLockboxHoursModal,
      closeLockboxHoursModal,
      access,
      supraLockbox,
      supraLockboxAccessHours,
      updateLockboxHours,
      selectTimes,
      listingTimezone,
    } = this.props;

    const isOldFirmwareLockbox = version => {
      if (!version) return false;
      return version.startsWith('06') || version.startsWith('08');
    };

    const shouldRenderLockboxHoursModal =
      access === 'Supra Lockbox' &&
      supraLockbox?.serialNumber &&
      supraLockboxAccessHours &&
      !isOldFirmwareLockbox(supraLockbox?.version);

    return (
      <Container>
        <AdvancedSelector
          advConfSetting={advConfSetting}
          clickHandler={this.advAutoConfirmClickHandler}
          disableAutoConfirm={disableAutoConfirm}
          noMoreShowings={this.props.noMoreShowings}
          access={access}
          supraLockbox={supraLockbox}
          supraLockboxAccessHours={supraLockboxAccessHours}
          openLockboxHoursModal={openLockboxHoursModal}
          isOldFirmwareLockbox={isOldFirmwareLockbox(supraLockbox?.version)}
        />
        <WeeklyTimeSelectorContainer
          className="weekly-time-selector"
          ref={el => {
            this.componentRef = el;
          }}
        >
          {this.renderSelectionBox()}

          <table>
            <thead>
              {supraLockbox?.productCode === 'iBox' &&
                moment().tz(listingTimezone).isDST() && (
                  <tr>
                    <td colSpan={8}>
                      <Alert
                        message="Daylight Savings Time (DST) in effect."
                        type="info"
                        showIcon
                      />
                    </td>
                  </tr>
                )}
              <tr>
                <td></td>
                {Object.keys(timeSelection).map(day => (
                  <th key={day}>
                    <h4
                      className={
                        (selectedColumns.includes(day) &&
                          (selectionBox.toValue === true ||
                          selectionBox.toValue === 'Available' ||
                          selectionBox.toValue === 'AutoConfirm'
                            ? 'weekly-time-selector__label--available'
                            : 'weekly-time-selector__label--restricted')) ||
                        ''
                      }
                    >
                      {day}
                    </h4>
                    <button
                      className="time-selector__toggle-day time-selector__toggle-day--off"
                      onClick={() => {
                        this.toggleDay(day, 'Restricted');
                      }}
                      data-cy={`${day}-set-restricted`}
                    />
                    <button
                      className="time-selector__toggle-day time-selector__toggle-day--on"
                      onClick={() => {
                        this.toggleDay(day, 'Available');
                      }}
                      data-cy={`${day}-set-available`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody onMouseDown={this.onMouseDown}>
              {Object.keys(timeSelection[Object.keys(timeSelection)[0]]).map(
                timeslot => (
                  <tr
                    key={timeslot}
                    className="weekly-time-selector__time-interval"
                  >
                    <td key={`${timeslot}-label`}>
                      <h4
                        className={
                          (selectedRows.includes(timeslot) &&
                            (selectionBox.toValue === true ||
                            selectionBox.toValue === 'Available' ||
                            selectionBox.toValue === 'AutoConfirm'
                              ? 'weekly-time-selector__label--available'
                              : 'weekly-time-selector__label--restricted')) ||
                          ''
                        }
                      >
                        {moment(timeslot, 'H:mm').format('h:mm a')}
                      </h4>
                    </td>

                    {Object.entries(timeSelection).map(
                      ([day, dayAvailabilities]) => (
                        <td
                          className="weekly-time-selector__timeslot"
                          key={`${day}-${timeslot}`}
                        >
                          <TimeSelectorButton
                            status={this.getTimeSelectorButtonStatus(
                              day,
                              dayAvailabilities,
                              timeslot,
                            )}
                            showIcon
                            day={day}
                            timeslot={timeslot}
                            access={access}
                            ref={el => {
                              // TODO: We only need refs for the first row and column
                              this.timeslotRefs[`${day}-${timeslot}`] = el;
                            }}
                          />
                        </td>
                      ),
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </WeeklyTimeSelectorContainer>
        {shouldRenderLockboxHoursModal && (
          <LockboxHoursModal
            visible={lockboxHoursModalVisible}
            lockboxType={supraLockbox?.productCode}
            closeModal={closeLockboxHoursModal}
            supraLockboxAccessHours={supraLockboxAccessHours}
            updateLockboxHours={updateLockboxHours}
            selectTimes={selectTimes}
            timeslots={Object.keys(Object.values(timeSelection)[0])}
          />
        )}
      </Container>
    );
  }
}

WeeklyTimeSelector.propTypes = {
  // { DAY_OF_WEEK: { TIME_SLOT: string||bool } }
  selectTimes: PropTypes.func,
};

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const WeeklyTimeSelectorContainer = styled.div`
  overflow-x: scroll;
`;

export default WeeklyTimeSelector;
